/** 履歴書・職務経歴書の保存データから、志望動機 AI 質問の初期値を組み立てる。 */

type RirekishoHistoryItem = {
  description?: string
  year?: number | string
  month?: number | string
}

export type RirekishoAutofillSource = {
  educationalHistories?: RirekishoHistoryItem[]
  licenseHistories?: RirekishoHistoryItem[]
  jobHistories?: RirekishoHistoryItem[]
}

type ShokurekishoJobHistory = {
  companyName?: string
  startYear?: number | string
  startMonth?: number | string
  endYear?: number | string
  endMonth?: number | string
  isContinuing?: boolean
  projects?: Array<{ contentsOfProject?: string }>
}

export type ShokurekishoAutofillSource = {
  jobSummary?: string
  jobHistories?: ShokurekishoJobHistory[]
}

export type ShibouDoukishoAiAutofill = {
  ai_education_detail?: string
  ai_licenses_detail?: string
  ai_job_detail?: string
}

function educationDetail(
  rirekisho: RirekishoAutofillSource
): string | undefined {
  const histories = rirekisho.educationalHistories
  if (!histories || histories.length === 0) return undefined
  const educations = histories.filter(
    (item) => item.description && item.description.includes('卒業')
  )
  if (educations.length === 0) return undefined
  const last = educations[educations.length - 1]
  return `${last.description} (${last.year}年${last.month}月)`
}

function licensesDetail(
  rirekisho: RirekishoAutofillSource
): string | undefined {
  const histories = rirekisho.licenseHistories
  if (!histories || histories.length === 0) return undefined
  const licenses = histories
    .filter((item) => item.description)
    .map((item) => {
      if (item.year && item.month) {
        return `${item.description} (${item.year}年${item.month}月取得)`
      }
      return item.description as string
    })
  return licenses.length > 0 ? licenses.join('、') : undefined
}

function jobDetailFromShokurekisho(
  shokurekisho: ShokurekishoAutofillSource
): string | undefined {
  if (shokurekisho.jobSummary) return shokurekisho.jobSummary
  const histories = shokurekisho.jobHistories
  if (!histories || histories.length === 0) return undefined
  const lastJob = histories[histories.length - 1]
  let jobDetail = ''
  if (lastJob.companyName) jobDetail += lastJob.companyName
  if (lastJob.startYear && lastJob.startMonth) {
    jobDetail += ` ${lastJob.startYear}年${lastJob.startMonth}月〜`
    if (lastJob.isContinuing) {
      jobDetail += '現在'
    } else if (lastJob.endYear && lastJob.endMonth) {
      jobDetail += `${lastJob.endYear}年${lastJob.endMonth}月`
    }
  }
  if (lastJob.projects && lastJob.projects.length > 0) {
    const project = lastJob.projects[0]
    if (project.contentsOfProject) {
      jobDetail += `\n${project.contentsOfProject}`
    }
  }
  return jobDetail || undefined
}

function jobDetailFromRirekisho(
  rirekisho: RirekishoAutofillSource
): string | undefined {
  const histories = rirekisho.jobHistories
  if (!histories || histories.length === 0) return undefined
  const jobs = histories
    .filter((item) => item.description)
    .map((item) => {
      if (item.year && item.month) {
        return `${item.year}年${item.month}月 ${item.description}`
      }
      return item.description as string
    })
  return jobs.length > 0 ? jobs.join('\n') : undefined
}

export function autofillShibouDoukishoAi(opts: {
  rirekisho?: RirekishoAutofillSource | null
  shokurekisho?: ShokurekishoAutofillSource | null
}): ShibouDoukishoAiAutofill {
  const patch: ShibouDoukishoAiAutofill = {}
  const rirekisho = opts.rirekisho
  if (rirekisho) {
    const education = educationDetail(rirekisho)
    if (education) patch.ai_education_detail = education
    const licenses = licensesDetail(rirekisho)
    if (licenses) patch.ai_licenses_detail = licenses
  }
  const fromShokurekisho = opts.shokurekisho
    ? jobDetailFromShokurekisho(opts.shokurekisho)
    : undefined
  if (fromShokurekisho) {
    patch.ai_job_detail = fromShokurekisho
  } else if (rirekisho) {
    const fromRirekisho = jobDetailFromRirekisho(rirekisho)
    if (fromRirekisho) patch.ai_job_detail = fromRirekisho
  }
  return patch
}
